import React, { useMemo } from 'react';
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
import { SparkJob } from '../types/sparkTypes';
import { getJobDisplayStatus } from '../hooks/useAnimatedProgress';
import styles from '../styles/GlobalJobPanel.module.css';

const DeepSightsIcon = () => (
  <svg focusable="false" aria-hidden="true" viewBox="0 0 20 19" width="20px" font-size="default"><path d="M4.04547 8.91712C3.28276 8.66939 2.36393 8.76671 1.77963 9.31525C1.51202 9.5674 1.32468 9.91245 1.31576 10.2752C1.29346 11.2351 2.39516 11.9385 3.26492 11.9695C4.38445 12.0137 5.56643 11.6244 6.6146 11.1776C5.96786 10.2088 5.18285 9.28871 4.04101 8.91712H4.04547Z" fill="#91EAE4"></path><path d="M10 18.8347C13.8002 18.8347 17.1097 16.729 18.8002 13.6324C18.6575 13.7386 18.5103 13.8448 18.3631 13.9465C17.7922 14.3358 17.1945 14.672 16.5656 14.9595C15.2409 15.5612 13.6976 15.9947 12.2302 15.9505C9.6744 15.8753 8.31847 13.9067 7.10081 11.9382C6.94469 11.686 6.78412 11.4339 6.61463 11.1817C5.562 11.6285 4.38448 12.0178 3.26494 11.9736C2.39072 11.9382 1.29349 11.2436 1.31579 10.2793C1.32471 9.91212 1.51204 9.56707 1.77966 9.31934C2.36396 8.7708 3.27832 8.67348 4.0455 8.92121C5.18733 9.2928 5.96789 10.2129 6.61909 11.1817C6.80643 11.1021 6.9893 11.0225 7.16771 10.9384C8.36307 10.3943 9.48707 9.71747 10.5888 9.01411C10.669 8.96545 10.7449 8.91236 10.8252 8.8637C9.02766 7.29329 7.85014 5.10799 7.21677 2.79882C7.06066 2.23701 6.92239 1.59557 6.45406 1.20186C5.48618 0.38348 4.00981 1.08242 3.21588 1.80349C2.49777 2.45377 2.03836 2.90499 1.49866 3.70567C1.47636 3.74549 1.4496 3.7853 1.42284 3.82069C0.517395 5.31148 0 7.05441 0 8.91679C0 14.3933 4.47815 18.8347 10 18.8347Z" fill="#86A8E7"></path><path d="M16.3738 6.431C14.5942 6.59025 12.9751 7.5148 11.4898 8.44378C11.2668 8.58534 11.0393 8.7269 10.8163 8.86845C11.3917 9.37275 12.025 9.80628 12.7342 10.169C13.1044 10.3592 13.4925 10.5185 13.885 10.6556C14.5005 10.868 15.1472 11.0228 15.7984 11.0715C16.0839 11.0936 16.3738 11.098 16.6548 11.0847C17.538 11.0449 18.4211 10.7928 19.1169 10.2531C19.6343 9.85494 19.9911 9.24004 19.9911 8.59419V8.51013C19.9822 8.30664 19.9376 8.09873 19.8439 7.89524C19.2864 6.63449 17.5959 6.32041 16.3738 6.431Z" fill="#91EAE4"></path><path d="M19.9911 8.5986C19.9911 8.62072 19.9911 8.64726 19.9866 8.66938C19.9599 9.28427 19.6164 9.8682 19.1169 10.2531C18.4255 10.7839 17.5558 11.0361 16.6815 11.0803C16.6726 11.0803 16.6682 11.0803 16.6592 11.0803C16.3738 11.0936 16.0883 11.0891 15.8029 11.067C15.1516 11.0184 14.5049 10.8635 13.8894 10.6512C13.4969 10.5185 13.1088 10.3548 12.7386 10.1646C12.0339 9.80627 11.3961 9.36833 10.8207 8.86845C10.7404 8.91711 10.6646 8.97019 10.5843 9.01885C9.4826 9.72222 8.3586 10.399 7.16324 10.9432C6.98483 11.0228 6.80196 11.1024 6.61462 11.1865C6.78412 11.4386 6.94469 11.6952 7.1008 11.9429C8.32292 13.9115 9.67439 15.8756 12.2301 15.9552C13.6976 15.9994 15.2409 15.5659 16.5656 14.9643C17.1945 14.6812 17.7966 14.3406 18.3631 13.9513C18.5103 13.8495 18.6574 13.7478 18.8002 13.6372C19.5629 12.2349 20 10.6291 20 8.92596C20 8.81979 19.9955 8.70919 19.9911 8.60745V8.5986Z" fill="#7F7FD5"></path></svg>
);

interface ActivityBadgeTriggerProps {
  isOpen: boolean;
  onToggle: () => void;
  quietCreativeTypeFqn?: string | null;
}

function isVisibleOnCurrentBoard(job: SparkJob, quietCreativeTypeFqn: string | null | undefined): boolean {
  if (!quietCreativeTypeFqn) {
    return false;
  }

  const displayStatus = getJobDisplayStatus(job);

  return (
    (
      (displayStatus === 'pending' || displayStatus === 'running') &&
      (job._source_type_fqn === quietCreativeTypeFqn || job._target_type_fqn === quietCreativeTypeFqn)
    ) ||
    (
      displayStatus === 'completed' &&
      !job._completion_seen_by_current_user &&
      job._result_kind === 'creation' &&
      job._target_type_fqn === quietCreativeTypeFqn &&
      !!job._source_type_fqn &&
      job._source_type_fqn !== quietCreativeTypeFqn
    )
  );
}

export const ActivityBadgeTrigger: React.FC<ActivityBadgeTriggerProps> = ({
  isOpen,
  onToggle,
  quietCreativeTypeFqn,
}) => {
  const jobs = useAtlasStore((state: AtlasState) => state.currentSparkJobs);

  const { activeCount, unseenFailureCount, unseenSuccessCount } = useMemo(() => {
    const visibleJobFqns = new Set(
      jobs
        .filter((job) => isVisibleOnCurrentBoard(job, quietCreativeTypeFqn))
        .map((job) => job.fqn)
    );

    const backgroundJobs = jobs.filter((job) => !visibleJobFqns.has(job.fqn));
    const unseenTerminalJobs = backgroundJobs.filter((job) =>
      !job._completion_seen_by_current_user &&
      (getJobDisplayStatus(job) === 'completed' || getJobDisplayStatus(job) === 'failed' || getJobDisplayStatus(job) === 'cancelled')
    );

    return {
      activeCount: backgroundJobs.filter((job) => getJobDisplayStatus(job) === 'pending' || getJobDisplayStatus(job) === 'running').length,
      unseenFailureCount: unseenTerminalJobs.filter((job) => getJobDisplayStatus(job) === 'failed' || getJobDisplayStatus(job) === 'cancelled').length,
      unseenSuccessCount: unseenTerminalJobs.filter((job) => getJobDisplayStatus(job) === 'completed').length,
    };
  }, [jobs, quietCreativeTypeFqn]);

  return (
    <button
      className={styles.jobPanelTrigger}
      onClick={onToggle}
      title={isOpen ? 'Hide Activity' : 'Show Activity'}
      type="button"
    >
      <span className={styles.triggerLabel}>Activity</span>
      {activeCount > 0 ? (
        <span className={`${styles.badge} ${styles.badgeActive} ${styles.pulsing}`}>{activeCount}</span>
      ) : unseenFailureCount > 0 ? (
        <span className={`${styles.badge} ${styles.badgeFailed}`}>{unseenFailureCount}</span>
      ) : unseenSuccessCount > 0 ? (
        <span className={`${styles.badge} ${styles.badgeSuccess}`}>{unseenSuccessCount}</span>
      ) : (
        <DeepSightsIcon />
      )}
    </button>
  );
};

export default ActivityBadgeTrigger;
